import Spinner from "./Spinner.js";
import Template from "../assets/PhotoMotionSpinner.html?raw";
import style from "../assets/main.scss?inline";
import "construct-style-sheets-polyfill";

const ALLOWED_ORIGINS = [
  "http://127.0.0.1",
  "http://localhost",
  "https://spinner.photo-motion.com",
  "https://spinner.photo-motion.io",
  "https://spinner.photo-motion.dev",
  "https://storage.googleapis.com/photomotion-spinner-data-dev",
  "https://storage.googleapis.com/photomotion-spinner-data-prod",
  "https://storage.googleapis.com/photomotion-spinner-data-acc",
];

const SPINNER_HASH = "spinner-hash";
const SPINNER_DATA = "spinner-data";
const SPINNER_URL = "spinner-url";
const CAN_SCROLL = "can-scroll";
const CAN_FULLSCREEN = "can-fullscreen";
const DEFAULT_LAYER = "default-layer";
const VIEWER_MODE = "viewer-mode";
const MAX_ZOOM = "max-zoom";
const SPINNING_SPEED = "spinning-speed";
const ACTIVE_COLUMN = "active-column";
const ACTIVE_ROW = "active-row";
const CLEAN_UI = "clean-ui";
const BUTTONS_PLACEMENT = "buttons-placement";
const HIDE_GALLERY = "hide-gallery";
const SHOW_GALLERY = "show-gallery";
const HIDE_DOORS = "hide-doors";
const HIDE_LIGHTS = "hide-lights";
const HIDE_SHOWCASE = "hide-showcase";
const HIDE_INTERIOR = "hide-interior";
const HIDE_ZOOM = "hide-zoom";
const HIDE_HOTSPOTS = "hide-hotspots";
const INSPECTOR_MODE = "inspector-mode";

const ATTRIBUTE_ALIASES = Object.freeze({
  hash: SPINNER_HASH,
  "article-hash": SPINNER_HASH,
  buttons: BUTTONS_PLACEMENT,
  scroll: CAN_SCROLL,
  fullscreen: CAN_FULLSCREEN,
  mode: VIEWER_MODE,
});

/**
 * @param {string} attributeName
 * @returns {string}
 */
function normalizeAttributeName(attributeName) {
  return ATTRIBUTE_ALIASES[attributeName] || attributeName;
}

let replacements = {
  VITE_BRAND_URL: import.meta.env.VITE_BRAND_URL,
};

export default class PhotoMotionSpinner extends HTMLElement {
  /**
   * @type {Spinner|null}
   */
  #spinner = null;
  #connected;
  #hasBeenConnected;
  #hashThrottle;
  #galleryThrottle;
  #uiConfigThrottle;
  #root;

  constructor() {
    super();
    this.#connected = new Promise((resolve) => {
      this.#hasBeenConnected = resolve;
    });
  }

  static get observedAttributes() {
    return [
      SPINNER_HASH,
      "article-hash",
      SPINNER_DATA,
      SPINNER_URL,
      CAN_SCROLL,
      CAN_FULLSCREEN,
      DEFAULT_LAYER,
      VIEWER_MODE,
      MAX_ZOOM,
      SPINNING_SPEED,
      ACTIVE_COLUMN,
      ACTIVE_ROW,
      CLEAN_UI,
      BUTTONS_PLACEMENT,
      HIDE_GALLERY,
      SHOW_GALLERY,
      HIDE_DOORS,
      HIDE_LIGHTS,
      HIDE_SHOWCASE,
      HIDE_INTERIOR,
      HIDE_ZOOM,
      HIDE_HOTSPOTS,
      INSPECTOR_MODE,
    ];
  }

  setAttribute(qualifiedName, value) {
    super.setAttribute(normalizeAttributeName(qualifiedName), value);
  }

  getAttribute(qualifiedName) {
    return super.getAttribute(normalizeAttributeName(qualifiedName));
  }

  hasAttribute(qualifiedName) {
    return super.hasAttribute(normalizeAttributeName(qualifiedName));
  }

  removeAttribute(qualifiedName) {
    super.removeAttribute(normalizeAttributeName(qualifiedName));
  }

  get activeColumn() {
    return parseInt(this.getAttribute(ACTIVE_COLUMN));
  }

  set activeColumn(value) {
    const parsedValue = parseInt(value);
    if (Number.isNaN(parsedValue)) {
      this.removeAttribute(ACTIVE_COLUMN);
      return;
    }

    if (this.getAttribute(ACTIVE_COLUMN) !== String(parsedValue)) {
      this.setAttribute(ACTIVE_COLUMN, parsedValue);
      return;
    }

    if (
      this.#spinner?.activeLayer &&
      parsedValue !== this.#spinner.activeColumn
    ) {
      this.#spinner.setSpinnerColumn(parsedValue);
    }
  }

  get activeRow() {
    return parseInt(this.getAttribute(ACTIVE_ROW));
  }

  set activeRow(value) {
    const parsedValue = parseInt(value);
    if (Number.isNaN(parsedValue)) {
      this.removeAttribute(ACTIVE_ROW);
      return;
    }

    if (this.getAttribute(ACTIVE_ROW) !== String(parsedValue)) {
      this.setAttribute(ACTIVE_ROW, parsedValue);
      return;
    }

    if (this.#spinner?.activeLayer && parsedValue !== this.#spinner.activeRow) {
      this.#spinner.setSpinnerRow(parsedValue);
    }
  }

  get inspectorMode() {
    return this.hasAttribute(INSPECTOR_MODE);
  }

  set inspectorMode(val) {
    if (val) {
      this.setAttribute(INSPECTOR_MODE, "");
    } else {
      this.removeAttribute(INSPECTOR_MODE);
    }

    if (this.#spinner) this.#spinner.inspectorMode = val;
  }

  get notFound() {
    return this.#spinner.notFound;
  }

  // Add not-found attribute to the web component element
  set notFound(val) {
    if (val) {
      this.setAttribute("not-found", "");
    } else {
      this.removeAttribute("not-found");
    }

    if (this.#spinner) this.#spinner.notFound = val;
  }

  get cleanUi() {
    return this.hasAttribute("clean-ui");
  }

  set cleanUi(val) {
    if (val) {
      this.setAttribute("clean-ui", "");
    } else {
      this.removeAttribute("clean-ui");
    }
  }

  get disabled() {
    return this.hasAttribute("disabled");
  }

  set disabled(val) {
    if (val) {
      this.setAttribute("disabled", "");
    } else {
      this.removeAttribute("disabled");
    }
  }

  connectedCallback() {
    const mode = ALLOWED_ORIGINS.some((origin) =>
      window.location.origin.startsWith(origin),
    )
      ? "open"
      : "closed";

    this.#root = this.shadowRoot ?? this.attachShadow({ mode });
    const sheet = new CSSStyleSheet();

    sheet
      .replace(
        ":host { display: block; position: relative; } :host([disabled]) { pointer-events:none } " +
          style,
      )
      .then(() => {
        this.#root.adoptedStyleSheets = [sheet];
      })
      .catch((e) => {
        console.error("Failed to load stylesheet", e);
      });

    let populatedTemplate = Template;

    for (let [key, value] of Object.entries(replacements)) {
      populatedTemplate = populatedTemplate.replaceAll(`%${key}%`, value);
    }

    this.#root.innerHTML = populatedTemplate;

    this.#hasBeenConnected();
  }

  disconnectedCallback() {
    /*
     * This is for the case where element is detached and might
     * be re-attached later
     */
    this.#connected = new Promise((resolve) => {
      this.#hasBeenConnected = resolve;
    });
  }

  attributeChangedCallback(attrName, oldVal, newVal) {
    switch (attrName) {
      case SPINNER_HASH:
      case "article-hash":
        this.spinnerHashChanged(newVal, oldVal);
        break;
      case SPINNER_DATA:
        this.spinnerDataChanged(newVal, oldVal);
        break;
      case SPINNER_URL:
        this.spinnerUrlChanged(newVal, oldVal);
        break;
      case ACTIVE_COLUMN:
        if (newVal !== null && newVal !== oldVal) {
          this.activeColumn = newVal;
        }
        break;
      case ACTIVE_ROW:
        if (newVal !== null && newVal !== oldVal) {
          this.activeRow = newVal;
        }
        break;
      default:
        this.updateUiConfig();
    }
  }

  /**
   *
   * @returns {UIConfig}
   */
  getUiConfig() {
    return {
      canScroll: this.getAttribute(CAN_SCROLL),
      canFullscreen: this.getAttribute(CAN_FULLSCREEN),
      defaultLayer: this.getAttribute(DEFAULT_LAYER),
      viewerMode: this.getAttribute(VIEWER_MODE),
      maxZoom: parseFloat(this.getAttribute(MAX_ZOOM) ?? 0),
      spinningSpeed: parseFloat(this.getAttribute(SPINNING_SPEED) ?? 0),
      activeColumn: this.hasAttribute(ACTIVE_COLUMN)
        ? parseInt(this.getAttribute(ACTIVE_COLUMN))
        : null,
      activeRow: this.hasAttribute(ACTIVE_ROW)
        ? parseInt(this.getAttribute(ACTIVE_ROW))
        : null,
      cleanUi: this.hasAttribute(CLEAN_UI),
      buttonsPlacement: this.getAttribute(BUTTONS_PLACEMENT),
      showGallery: this.hasAttribute(SHOW_GALLERY),
      hideGallery: this.hasAttribute(HIDE_GALLERY),
      hideDoors: this.hasAttribute(HIDE_DOORS),
      hideLights: this.hasAttribute(HIDE_LIGHTS),
      hideShowcase: this.hasAttribute(HIDE_SHOWCASE),
      hideInterior: this.hasAttribute(HIDE_INTERIOR),
      hideZoom: this.hasAttribute(HIDE_ZOOM),
      hideHotspots: this.hasAttribute(HIDE_HOTSPOTS),
    };
  }

  spinnerHashChanged(value, oldValue) {
    if (value === oldValue) return;

    this.#connected
      .then(() => {
        if (!this.#spinner) {
          this.#spinner = new Spinner(this.#root);
        }

        cancelAnimationFrame(this.#hashThrottle);
        this.#hashThrottle = requestAnimationFrame(() => {
          this.#spinner.setHash(value, this.getUiConfig()).catch((e) => {
            console.error(e);
            this.notFound = true;
          });
        });
      })
      .catch(console.error);
  }

  spinnerDataChanged(value, oldValue) {
    if (value?.startsWith("object:")) return;
    if (value === oldValue) return;

    this.#connected
      .then(() => {
        if (!this.#spinner) {
          this.#spinner = new Spinner(this.#root);
        }

        cancelAnimationFrame(this.#hashThrottle);
        this.#hashThrottle = requestAnimationFrame(() => {
          const spinnerData = JSON.parse(value);

          if (Object.keys(spinnerData).length === 0) {
            this.notFound = true;
            return;
          }

          this.setAttribute(
            SPINNER_DATA,
            `object:${Object.keys(spinnerData).join(",")}`,
          );
          this.#spinner.setData(spinnerData);
          this.#spinner.applyUiConfig(this.getUiConfig());
        });
      })
      .catch(console.error);
  }

  spinnerUrlChanged(value, oldValue) {
    if (value === oldValue) return;

    this.#connected
      .then(() => {
        if (!this.#spinner) {
          this.#spinner = new Spinner(this.#root);
        }

        cancelAnimationFrame(this.#hashThrottle);
        this.#hashThrottle = requestAnimationFrame(() => {
          if (
            !value ||
            !ALLOWED_ORIGINS.some((origin) => value.startsWith(origin))
          ) {
            this.notFound = true;
            return;
          }

          fetch(value)
            .then((response) => response.json())
            .then((data) => {
              this.notFound = false;
              this.#spinner.setData(data);
              this.#spinner.applyUiConfig(this.getUiConfig());
            })
            .catch((e) => {
              console.error(e);
              this.notFound = true;
            });
        });
      })
      .catch(console.error);
  }

  updateUiConfig() {
    if (!this.#spinner) {
      return;
    }

    this.#connected
      .then(() => {
        cancelAnimationFrame(this.#uiConfigThrottle);
        this.#uiConfigThrottle = requestAnimationFrame(() => {
          this.#spinner.inspectorMode = this.inspectorMode;
          this.#spinner.notFound = this.notFound;
          this.#spinner.applyUiConfig(this.getUiConfig());
        });
      })
      .catch(console.error);
  }
}
